import React from "react";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import PersonBadge from "components/PersonBadge";
import InconclusiveBadge from "components/InconclusiveBadge";
import Colors from "lookup/Colors.json";
import { FAX_DISPOSITION_PROGRAM_CODES } from "views/Raw/FaxDisposition/FaxDispositionConfig";

export const RAW_STATUSES = {
  READY: "READY",
  UNCLAIMED: "UNCLAIMED",
  CLAIMED: "CLAIMED",
  CONFIRMED: "CONFIRMED",
};

const detectedDispositionColors = {
  ACCEPTED: Colors["rm-success"],
  DECLINED: Colors["rm-danger"],
  UNKNOWN: Colors["rm-warning"],
};

const isOwnedByLoggedInUser = (details, loggedInUserId) =>
  details.ownerId === loggedInUserId;

const isOwnedByAnyone = (details) =>
  details.ownerId !== 0 && details.ownerId !== null;

const renderOwnerInformation = (details, loggedInUserId) => {
  if (details.status === RAW_STATUSES.CONFIRMED) {
    return (
      <Alert variant="info">
        Disposition confirmed by{" "}
        <PersonBadge variant="info" name={details.ownerName} />
        <strong id="dispositionConfirmedBy">
          {details.ownerName || "Anonymous"}
        </strong>
      </Alert>
    );
  }

  if (!isOwnedByAnyone(details)) {
    return <Alert variant="danger">Unclaimed</Alert>;
  }

  const variant = isOwnedByLoggedInUser(details, loggedInUserId)
    ? "success"
    : "warning";

  if (details.referrerId) {
    return (
      <Alert variant={variant}>
        <span>
          Referred by{" "}
          <PersonBadge variant={variant} name={details.referrerName} />
          <strong id="dispositionReferredBy">{details.referrerName}</strong> to
          <PersonBadge variant={variant} name={details.ownerName} />
          <strong id="dispositionOwner">{details.ownerName}</strong>
          <br />
          <span id="dispositionNotes" style={{ fontSize: "0.9em" }}>
            <strong>Notes: </strong> {details.statusNote}
          </span>
        </span>
      </Alert>
    );
  }

  return (
    <Alert variant={variant}>
      <span id="dispositionClaimedByContainer">
        {details.autoClaimed ? "Auto-c" : "C"}laimed by{" "}
        <PersonBadge variant={variant} name={details.ownerName} />
        <span id="dispositionOwner">{details.ownerName}</span>
      </span>
    </Alert>
  );
};

const renderBreadcrumb = (
  headerConfig,
  rawDetails,
  faxProgramCode,
  contextIsRaw,
  listQueryParams
) => {
  const listLink = headerConfig.getListLink(
    faxProgramCode,
    contextIsRaw,
    listQueryParams
  );

  if (!contextIsRaw && headerConfig.getSwimBreadcrumb) {
    const swimBreadcrumb = headerConfig.getSwimBreadcrumb(rawDetails);

    return (
      <Breadcrumb>
        <Breadcrumb.Item
          href={listLink.href}
          linkProps={{ "data-testid": "raw-list-breadcrumb-link" }}
        >
          {listLink.text}
        </Breadcrumb.Item>
        <Breadcrumb.Item href={swimBreadcrumb.switchHref}>
          {swimBreadcrumb.switchLabel}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>
          {headerConfig.getActiveBreadcrumbText(rawDetails, contextIsRaw)}
        </Breadcrumb.Item>
      </Breadcrumb>
    );
  }

  return (
    <Breadcrumb>
      <Breadcrumb.Item
        href={listLink.href}
        linkProps={{ "data-testid": "raw-list-breadcrumb-link" }}
      >
        {listLink.text}
      </Breadcrumb.Item>
      <Breadcrumb.Item active>
        {headerConfig.getActiveBreadcrumbText(rawDetails, contextIsRaw)}
      </Breadcrumb.Item>
    </Breadcrumb>
  );
};

const getStdConfirmedDispositionDisplay = (disposition) => {
  if (!disposition?.disposition) {
    return null;
  }

  return {
    text: disposition.disposition,
    borderColor:
      detectedDispositionColors[disposition.disposition] ?? Colors["rm-warning"],
    inconclusiveResult: disposition.inconclusiveResult,
    responseTypeList: disposition.responseTypeList,
  };
};

const resolveDispositionBox = ({
  faxProgramCode,
  isRawConfirmed,
  changeDisposition,
  disposition,
  detectedDisposition,
  isInconclusive,
  responseTypeList,
  detectedDispositionContainerId,
}) => {
  if (changeDisposition) {
    return null;
  }

  if (faxProgramCode === FAX_DISPOSITION_PROGRAM_CODES.DMR) {
    if (!detectedDisposition) {
      return null;
    }

    return {
      label: isRawConfirmed ? "Confirmed Disposition:" : "Auto Accept Detection:",
      text: detectedDisposition,
      borderColor:
        detectedDispositionColors[detectedDisposition] ?? Colors["rm-warning"],
      inconclusiveResult: isInconclusive,
      responseTypeList,
      containerId: isRawConfirmed
        ? "confirmedDispositionContainer"
        : detectedDispositionContainerId,
      textId: isRawConfirmed ? "confirmedDisposition" : "detectedDisposition",
    };
  }

  if (isRawConfirmed) {
    const confirmedDisplay = getStdConfirmedDispositionDisplay(disposition);
    if (!confirmedDisplay) {
      return null;
    }

    return {
      label: "Confirmed Disposition:",
      ...confirmedDisplay,
      containerId: "confirmedDispositionContainer",
      textId: "confirmedDisposition",
    };
  }

  if (!detectedDisposition) {
    return null;
  }

  return {
    label: "Auto Accept Detection:",
    text: detectedDisposition,
    borderColor:
      detectedDispositionColors[detectedDisposition] ?? Colors["rm-warning"],
    inconclusiveResult: isInconclusive,
    responseTypeList,
    containerId: detectedDispositionContainerId,
    textId: "detectedDisposition",
  };
};

const FaxDispositionHeader = ({
  headerConfig,
  rawDetails,
  rawId,
  faxProgramCode,
  contextIsRaw,
  listQueryParams = "",
  loggedInUserId,
  changeDisposition,
  detectedDisposition,
  disposition,
  isInconclusive,
  responseTypeList,
  detectedDispositionContainerId = "faxDispositionContainer",
}) => {
  const dispositionForConfig = headerConfig.dispositionFor;
  const displayId = dispositionForConfig.getDisplayId(rawDetails);
  const isRawConfirmed = rawDetails.status === RAW_STATUSES.CONFIRMED;
  const dispositionBox = resolveDispositionBox({
    faxProgramCode,
    isRawConfirmed,
    changeDisposition,
    disposition,
    detectedDisposition,
    isInconclusive,
    responseTypeList,
    detectedDispositionContainerId,
  });

  return (
    <>
      {renderBreadcrumb(
        headerConfig,
        rawDetails,
        faxProgramCode,
        contextIsRaw,
        listQueryParams
      )}

      {headerConfig.showSwimForwardBadge &&
      rawDetails.workflowItemStatus === "PENDING_RESPONSE_REVIEW" ? (
        <Row>
          <Col xs={12}>
            <p style={{ fontSize: "1.2em" }}>
              <Badge bg="warning" pill>
                Forwarded from SWIM
              </Badge>{" "}
              &nbsp;
              {rawDetails.forwardToPawNote ? (
                <small>
                  <strong>Comment:</strong> {rawDetails.forwardToPawNote}
                </small>
              ) : null}
            </p>
          </Col>
        </Row>
      ) : null}

      <Row>
        <Col
          id="dispositionOwnerInfo"
          md={12}
          style={{ marginTop: 12, marginBottom: 12 }}
        >
          {renderOwnerInformation(rawDetails, loggedInUserId)}
        </Col>
        <Col
          id="dispositionForContainer"
          md={12}
          style={{ fontSize: "1.5em" }}
        >
          <span className="text-muted">Disposition for </span>
          {contextIsRaw && dispositionForConfig.getLinkHref ? (
            <a href={dispositionForConfig.getLinkHref(rawDetails, rawId)}>
              <u>
                {dispositionForConfig.label} # {displayId}
              </u>
            </a>
          ) : (
            <span>
              {dispositionForConfig.label} #{" "}
              <span id="globalSwitchId">
                {dispositionForConfig.getSwimDisplayId
                  ? dispositionForConfig.getSwimDisplayId(rawDetails)
                  : displayId}
              </span>
            </span>
          )}
          &nbsp;
          {dispositionForConfig.showControlledBadge && rawDetails.isControlled ? (
            <Badge bg="warning" pill>
              Controlled
            </Badge>
          ) : null}
        </Col>
      </Row>

      <hr />

      {dispositionBox ? (
        <div
          id={dispositionBox.containerId}
          style={{
            border: `2px solid ${dispositionBox.borderColor}`,
            borderRadius: "10px",
            padding: 10,
          }}
          className="text-center top-spacer bottom-spacer"
        >
          {dispositionBox.label}{" "}
          <span
            id={dispositionBox.textId}
            style={{
              fontSize: "1.2em",
              letterSpacing: "1px",
              textDecoration: "underline",
            }}
          >
            {dispositionBox.text}
            <InconclusiveBadge
              inconclusiveResult={dispositionBox.inconclusiveResult}
              responseTypeList={dispositionBox.responseTypeList}
            />
          </span>
        </div>
      ) : null}
    </>
  );
};

export default FaxDispositionHeader;
